import type {
  PriceModalInput,
  PriceModalPlanInput,
  PriceModalDeviceInput,
  PriceModalPeriodTotalFromBackend,
  PriceModalResult,
  PriceModalSubscriptionRowModel,
  PriceModalPeriodRowModel,
  PriceModalSummaryModel,
} from '~/models/pricing/PriceDetailsModalTypes.model';
import { formatPrice } from '~/utils/pdp/priceHelpers';

function buildPriceDetailsModalBindingInfoText(
  bindingPeriodMonths: number,
  offerValidityInMonthsDiscount: number,
): string {
  const bindingText =
    bindingPeriodMonths === 0
      ? '0 mån bindning'
      : `${bindingPeriodMonths} mån bindning`;

  if (offerValidityInMonthsDiscount > 0) {
    return `${bindingText}, rabatten gäller i ${offerValidityInMonthsDiscount} månader`;
  }

  return bindingText;
}

export function buildModalSubscriptionRows(
  plans: PriceModalPlanInput[],
  bindingPeriodMonths: number,
): PriceModalSubscriptionRowModel[] {
  const rows: PriceModalSubscriptionRowModel[] = [];

  let familyPlanIndex = 1;

  for (const plan of plans) {
    const info = buildPriceDetailsModalBindingInfoText(
      bindingPeriodMonths,
      plan.offerValidityInMonthsDiscount,
    );
    const price = formatPrice(plan.monthlyFee);
    const regularPrice =
      plan.regularMonthlyFee > plan.monthlyFee
        ? formatPrice(plan.regularMonthlyFee)
        : undefined;

    for (let i = 0; i < plan.quantity; i++) {
      rows.push({
        name: setPlanNameForRow(plan, familyPlanIndex),
        info,
        regularPrice,
        price,
        priceUnit: 'kr/mån',
        dataAllowance: plan.dataAllowance,
        regularDataAllowance: plan.regularDataAllowance,
      });
    }

    if (plan.isFamily) {
      familyPlanIndex += 1;
    }
  }

  return rows;
}

function buildPeriodRowsFromBackendTotals(
  periodTotals: PriceModalPeriodTotalFromBackend[],
  thereafterOverride?: number,
): PriceModalPeriodRowModel[] {
  const rows: PriceModalPeriodRowModel[] = [];
  let currentMonthStart = 1;

  for (const period of periodTotals) {
    const label =
      currentMonthStart === period.periodEndMonth
        ? `Månad ${currentMonthStart}`
        : `Månad ${currentMonthStart}-${period.periodEndMonth}`;

    const regularPrice =
      period.regularMonthlyTotal !== undefined &&
      period.regularMonthlyTotal > period.monthlyTotal
        ? formatPrice(period.regularMonthlyTotal)
        : undefined;

    rows.push({
      label,
      regularPrice,
      price: formatPrice(period.monthlyTotal),
      priceUnit: 'kr/mån',
    });

    currentMonthStart = period.periodEndMonth + 1;
  }

  const lastPeriod = periodTotals[periodTotals.length - 1];
  const thereafterTotal =
    thereafterOverride ??
    lastPeriod.regularMonthlyTotal ??
    lastPeriod.monthlyTotal;

  rows.push({
    label: 'Därefter',
    regularPrice: undefined,
    price: formatPrice(thereafterTotal),
    priceUnit: 'kr/mån',
  });

  return rows;
}

export function buildModalPeriodRows(
  periodTotalsFromBackend?: PriceModalPeriodTotalFromBackend[],
  thereafterOverride?: number,
): PriceModalPeriodRowModel[] {
  if (periodTotalsFromBackend && periodTotalsFromBackend.length > 0) {
    return buildPeriodRowsFromBackendTotals(
      periodTotalsFromBackend,
      thereafterOverride,
    );
  }

  return [];
}

function buildModalSummary(input: PriceModalInput): PriceModalSummaryModel {
  const totalSavingsFromBackendSavings =
    input.totalSavingsFromBackend && input.totalSavingsFromBackend > 0
      ? input.totalSavingsFromBackend
      : null;
  const minimumCost = input.totalCostFromBackend ?? 0;

  let savingsPerMonth: string | null = null;
  if (
    input.savingsPerMonthFromBackend &&
    input.savingsPerMonthFromBackend > 0
  ) {
    savingsPerMonth = formatPrice(input.savingsPerMonthFromBackend);
  } else if (totalSavingsFromBackendSavings && input.bindingPeriodMonths > 0) {
    const perMonth = Math.round(
      totalSavingsFromBackendSavings / input.bindingPeriodMonths,
    );

    if (perMonth > 0) {
      savingsPerMonth = formatPrice(perMonth);
    }
  }

  return {
    totalSavings: totalSavingsFromBackendSavings
      ? formatPrice(totalSavingsFromBackendSavings)
      : null,
    savingsPerMonth,
    minimumTotalCost: formatPrice(minimumCost),
  };
}

function buildDeviceRow(
  device: PriceModalDeviceInput,
  bindingPeriodMonths: number,
): PriceModalSubscriptionRowModel {
  const info = device.isDirectPayment
    ? 'att betala nu'
    : `med ${device.paymentTimeMonths} mån delbetalning`;

  const priceUnit = bindingPeriodMonths === 0 ? 'kr' : 'kr/mån';

  const regularPrice =
    bindingPeriodMonths !== 0 && device.regularDownPayment > device.downPayment
      ? formatPrice(device.regularDownPayment)
      : undefined;

  return {
    name: device.productName,
    info,
    regularPrice,
    price: formatPrice(device.downPayment),
    priceUnit,
  };
}

export function buildPriceModal(input: PriceModalInput): PriceModalResult {
  return {
    subscriptionRows: buildModalSubscriptionRows(
      input.plans,
      input.bindingPeriodMonths,
    ),
    deviceRow: input.device
      ? buildDeviceRow(input.device, input.bindingPeriodMonths)
      : undefined,
    periodRows: buildModalPeriodRows(
      input.periodTotalsFromBackend,
      input.thereafterMonthlyTotal,
    ),
    summary: buildModalSummary(input),
  };
}

const setPlanNameForRow = (
  plan: PriceModalPlanInput,
  index: number,
): string => {
  if (!plan.isFamily) {
    return plan.planName;
  }

  return `Familjeabonnemang ${index}`;
};
